import { Controller } from "@hotwired/stimulus"
import { Turbo } from "@hotwired/turbo-rails"

declare global {
  interface Window {
    umami?: {
      track: (event: string, data?: Record<string, unknown>) => void;
    };
  }
}

export default class extends Controller {
  static targets = ["checkbox", "count", "actions", "compareBtn"]
  static values = {
    active: { type: Boolean, default: false },
    compareUrl: String,
  }

  declare checkboxTargets: HTMLInputElement[]
  declare countTarget: HTMLElement
  declare actionsTarget: HTMLElement
  declare compareBtnTarget: HTMLAnchorElement
  declare activeValue: boolean
  declare compareUrlValue: string

  private selectedIds: Set<string> = new Set()

  toggle() {
    this.activeValue = !this.activeValue
    if (!this.activeValue) {
      this.selectedIds.clear()
      this.checkboxTargets.forEach(cb => cb.checked = false)
    }
    this.updateUI()
  }

  select(event: Event) {
    const checkbox = event.target as HTMLInputElement
    const id = checkbox.value
    if (checkbox.checked) {
      this.selectedIds.add(id)
    } else {
      this.selectedIds.delete(id)
    }
    this.updateUI()
  }

  compare() {
    if (this.selectedIds.size < 2) return
    const ids = Array.from(this.selectedIds).join(",")
    try { window.umami?.track("arena-compare-multi-run", { count: this.selectedIds.size, ids }) } catch {}
    const url = `${this.compareUrlValue}?ids=${ids}`
    Turbo.visit(url)
  }

  cancel() {
    this.activeValue = false
    this.selectedIds.clear()
    this.checkboxTargets.forEach(cb => cb.checked = false)
    this.updateUI()
  }

  keydown(event: KeyboardEvent) {
    if (event.key === "Escape" && this.activeValue) {
      this.cancel()
    }
  }

  private updateUI() {
    // Toggle checkbox visibility
    this.checkboxTargets.forEach(cb => {
      const wrapper = cb.closest("[data-compare-checkbox]")
      if (wrapper) wrapper.classList.toggle("hidden", !this.activeValue)
    })

    // Update count and actions bar
    const count = this.selectedIds.size
    this.countTarget.textContent = `${count} selected`
    this.actionsTarget.classList.toggle("hidden", !this.activeValue)
    this.compareBtnTarget.classList.toggle("btn-disabled", count < 2)

    // Highlight selected rows — separate toggle calls (classList.toggle doesn't support spaces)
    this.checkboxTargets.forEach(cb => {
      const row = cb.closest("[data-compare-row]")
      if (row) {
        row.classList.toggle("ring-2", cb.checked)
        row.classList.toggle("ring-primary", cb.checked)
      }
    })
  }
}
